import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import BattleReveal from "@/components/BattleReveal";
import { Loading } from "@/components/battle/shared";
import { SallyInterrupt } from "@/components/battle/SallyInterrupt";
import { PersonalSetup } from "@/components/battle/PersonalSetup";
import { CommandSetup } from "@/components/battle/CommandSetup";
import { ResultView } from "@/components/battle/BattleResults";

export default function Battle() {
  const { state, refreshState } = useAuth();
  const navigate = useNavigate();
  const [opts, setOpts] = useState(null);
  const [phase, setPhase] = useState("setup"); // setup | sally | resolved
  const [result, setResult] = useState(null);
  const [revealPlaying, setRevealPlaying] = useState(false);

  // Personal mode
  const [approach, setApproach] = useState(null);
  const [target, setTarget] = useState(null);
  const [tactic, setTactic] = useState(null);
  const [personalForce, setPersonalForce] = useState(0);  // for send_personal_army
  const [personalBrief, setPersonalBrief] = useState(null);
  const [loadingPersonalBrief, setLoadingPersonalBrief] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [planRationale, setPlanRationale] = useState(null);
  const [customTactic, setCustomTactic] = useState("");

  // Command mode
  const [engagement, setEngagement] = useState(50);
  const [commitment, setCommitment] = useState(60);
  const [surround, setSurround] = useState(false);
  const [pveTactic, setPveTactic] = useState("overwhelm");
  const [assignedOfficerId, setAssignedOfficerId] = useState(null);
  const [aliveOfficers, setAliveOfficers] = useState([]);
  const [briefing, setBriefing] = useState(null);
  const [loadingBrief, setLoadingBrief] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [decidingSally, setDecidingSally] = useState(false);

  // Fetch officer roster for the commit picker
  useEffect(() => {
    api.get("/officers/roster")
      .then(({ data }) => setAliveOfficers(data.alive || []))
      .catch(() => {});
  }, []);

  const fetchOpts = useCallback(async () => {
    const { data } = await api.get("/battle/options");
    setOpts(data);
    if (data.pending_sally) setPhase("sally");
  }, []);

  useEffect(() => { fetchOpts(); }, [fetchOpts]);

  // Auto-fetch personal briefing when all 3 selections are locked. Re-fetches
  // when tactic.slider value changes too (force size affects intel).
  useEffect(() => {
    if (!approach || !target || !tactic) return;
    const ac = new AbortController();
    setLoadingPersonalBrief(true);
    setPersonalBrief(null);
    const force = tactic.key === "send_personal_army" ? personalForce : 0;
    api.post("/battle/personal/briefing", {
      approach: approach.key, target: target.key, tactic: tactic.key,
      personal_force: force,
    }, { signal: ac.signal })
      .then(({ data }) => setPersonalBrief(data.briefing))
      .catch((e) => { if (!ac.signal.aborted) setPersonalBrief(null); })
      .finally(() => { if (!ac.signal.aborted) setLoadingPersonalBrief(false); });
    return () => ac.abort();
  // Only the keys + force value drive re-fetch; flavour-id is implicit.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approach?.key, target?.key, tactic?.key, personalForce]);

  if (!state || !opts) return <Loading />;

  const isPersonal = opts.mode === "personal";

  // ------- PERSONAL MODE actions -------
  const submitPersonal = async () => {
    if (!approach || !target || !tactic) {
      toast.error("Choose approach, target, and tactic.");
      return;
    }
    setResolving(true);
    try {
      const { data } = await api.post("/battle/personal", {
        approach: approach.key, target: target.key, tactic: tactic.key,
        personal_force: tactic.key === "send_personal_army" ? personalForce : 0,
        custom_tactic: tactic.key === "other" ? customTactic.trim() : null,
      });
      setResult(data.result);
      setPhase("resolved");
      setRevealPlaying(true);
      await refreshState();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed.");
    } finally {
      setResolving(false);
    }
  };

  const suggestPlan = async () => {
    setSuggesting(true);
    setPlanRationale(null);
    try {
      const { data } = await api.post("/battle/personal/suggest_plan");
      const plan = data.plan;
      const a = (opts.approaches || []).find((o) => o.key === plan.approach);
      const t = (opts.targets || []).find((o) => o.key === plan.target);
      const c = (opts.tactics || []).find((o) => o.key === plan.tactic);
      if (a) setApproach(a);
      if (t) setTarget(t);
      if (c) setTactic(c);
      if (plan.tactic === "send_personal_army") {
        setPersonalForce(Math.max(1, Math.min(state.personal_horde - 1,
                                               plan.personal_force || Math.floor(state.personal_horde / 2))));
      } else {
        setPersonalForce(0);
      }
      setPlanRationale(plan.rationale);
      toast.success("Quartermaster recommends a plan.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No plan available.");
    } finally {
      setSuggesting(false);
    }
  };

  // ------- COMMAND MODE actions -------
  const fetchBrief = async () => {
    setLoadingBrief(true);
    try {
      const { data } = await api.post("/battle/briefing", { engagement, commitment, surround });
      setBriefing(data.briefing);
    } catch (e) {
      toast.error("Briefing failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoadingBrief(false);
    }
  };

  const resolveCommand = async () => {
    setResolving(true);
    try {
      const { data } = await api.post("/battle/resolve",
        { engagement, commitment, surround, tactic: pveTactic, assigned_officer_id: assignedOfficerId });
      setResult(data.result);
      if (data.result.phase === "sally") {
        setPhase("sally");
      } else if (data.result.rejected) {
        toast.error(data.result.narrative_summary);
      } else {
        setPhase("resolved");
        setRevealPlaying(true);
      }
      await refreshState();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed.");
    } finally {
      setResolving(false);
    }
  };

  const decideSally = async (retreat) => {
    setDecidingSally(true);
    try {
      const { data } = await api.post("/battle/sally_decide", { retreat });
      setResult(data.result);
      setPhase("resolved");
      setRevealPlaying(true);
      await refreshState();
      await fetchOpts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed.");
    } finally {
      setDecidingSally(false);
    }
  };

  const reset = () => {
    setPhase("setup");
    setBriefing(null);
    setResult(null);
    setApproach(null); setTarget(null); setTactic(null);
    setPersonalForce(0);
    setPersonalBrief(null);
    setCustomTactic("");
    fetchOpts();
  };

  const committedCount = Math.max(1, Math.round((state.personal_horde * commitment) / 100));
  const projectedHumanMorale = (opts.preview?.human_morale || 70) - (surround ? 25 : 0);
  const surroundEligible = commitment >= 60;

  return (
    <div className="min-h-screen">
      <NavBar active="battle" />

      {phase === "sally" && (
        <SallyInterrupt result={result} decideSally={decideSally} decidingSally={decidingSally} />
      )}

      {phase === "setup" && isPersonal && (
        <PersonalSetup
          navigate={navigate} opts={opts} state={state}
          approach={approach} setApproach={setApproach}
          target={target} setTarget={setTarget}
          tactic={tactic} setTactic={setTactic}
          personalForce={personalForce} setPersonalForce={setPersonalForce}
          customTactic={customTactic} setCustomTactic={setCustomTactic}
          personalBrief={personalBrief} loadingPersonalBrief={loadingPersonalBrief}
          suggesting={suggesting} suggestPlan={suggestPlan} planRationale={planRationale}
          submitPersonal={submitPersonal} resolving={resolving}
        />
      )}

      {phase === "setup" && !isPersonal && (
        <CommandSetup
          navigate={navigate} opts={opts} state={state}
          engagement={engagement} setEngagement={setEngagement}
          commitment={commitment} setCommitment={setCommitment}
          surround={surround} setSurround={setSurround}
          pveTactic={pveTactic} setPveTactic={setPveTactic}
          assignedOfficerId={assignedOfficerId} setAssignedOfficerId={setAssignedOfficerId}
          aliveOfficers={aliveOfficers}
          briefing={briefing} loadingBrief={loadingBrief} fetchBrief={fetchBrief}
          resolveCommand={resolveCommand} resolving={resolving}
          committedCount={committedCount}
          projectedHumanMorale={projectedHumanMorale}
          surroundEligible={surroundEligible}
        />
      )}

      {phase === "resolved" && result && (
        <ResultView result={result} onReset={reset} navigate={navigate} isPersonal={isPersonal} />
      )}

      {phase === "resolved" && result && revealPlaying && (
        <BattleReveal
          result={result}
          isPersonal={isPersonal}
          onDismiss={() => setRevealPlaying(false)}
        />
      )}
    </div>
  );
}
